import React from "react";
import { createPortal } from "react-dom";
import { sydneyWhen } from "./time.js";

// The owner report's full document: a summary first, then every detail horse
// by horse. It renders exactly what the host built (vendor.reports.document@1
// or a preview's frozen document) and derives no figure of its own. A figure
// the host could not source arrives as null or "unavailable" and says so here;
// it is never shown as zero.

const when = value => (value ? sydneyWhen(value) : "Not recorded");

function duration(ms) {
  if (ms == null) return "Not recorded";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function horseName(horse) {
  const looks = [horse.colour, horse.sex].filter(Boolean).join(" ");
  const breeding = [horse.sire, horse.dam].filter(Boolean).join(" × ");
  return [`Lot ${horse.number}`, looks, breeding].filter(Boolean).join(" · ");
}

const count = value => (value == null ? <span className="muted">n/a</span> : value);

// The summary as a table (owner, 25 Sep 2026): one row per horse, then all horses.
// It carries figures only; names are in the details below.
function SummaryTable({ report }) {
  const { includes, horses } = report;
  const cells = summary => <>
    {includes.viewing && <>
      <td className="report-num">{count(summary.xrayViews)}</td><td className="report-num">{count(summary.scopeViews)}</td>
      <td className="report-num">{count(summary.reportViews)}</td><td className="report-num">{count(summary.uniqueVets)}</td>
      <td>{summary.xrayViews == null ? count(null) : when(summary.lastViewedAt)}</td>
    </>}
    {includes.inspections && <>
      <td className="report-num">{summary.inspectionsToDate == null ? count(null)
        : `${summary.inspectionsToday} today, ${summary.inspectionsToDate} to date`}</td>
      <td>{summary.inspectionsToDate ? when(summary.lastInspectedAt) : "None yet"}</td>
    </>}
  </>;
  return <table className="report-doc-table report-doc-summary-table">
    <thead><tr><th scope="col">Lot</th><th scope="col">Horse</th>
      {includes.viewing && <><th scope="col">X-ray</th><th scope="col">Scope</th><th scope="col">Report</th>
        <th scope="col">Vets</th><th scope="col">Last viewed</th></>}
      {includes.inspections && <><th scope="col">Inspections</th><th scope="col">Last inspected</th></>}
    </tr></thead>
    <tbody>
      {horses.map(horse => <tr key={horse.lotId}>
        <th scope="row" className="report-doc-lotcell">{horse.number}</th>
        <td className="report-doc-horsecell"><strong>{[horse.colour, horse.sex].filter(Boolean).join(" ") || `Lot ${horse.number}`}</strong>
          {(horse.sire || horse.dam) && <span className="report-doc-breeding">{[horse.sire, horse.dam].filter(Boolean).join(" × ")}</span>}</td>
        {cells(horse.summary)}
      </tr>)}
      {horses.length > 1 && <tr className="report-doc-total"><th scope="row" colSpan={2}>All horses</th>{cells(report.totals)}</tr>}
    </tbody>
  </table>;
}

function Viewing({ horse }) {
  if (horse.viewing == null) return <p className="muted">Viewing figures are not available right now.</p>;
  if (!horse.viewing.length) return <p className="muted">No vet has viewed this horse yet.</p>;
  return <table className="report-doc-table">
    <caption>Viewing by vet</caption>
    <thead><tr><th scope="col">Vet</th><th scope="col">X-ray</th><th scope="col">Scope</th>
      <th scope="col">Report</th><th scope="col">X-ray time</th><th scope="col">Last activity</th></tr></thead>
    <tbody>{horse.viewing.map((vet, index) => <tr key={`${vet.vetName}-${index}`}>
      <th scope="row">{vet.vetName}</th><td className="report-num">{vet.xrayViews}</td>
      <td className="report-num">{vet.scopeViews}</td><td className="report-num">{vet.reportViews}</td>
      <td className="report-num">{duration(vet.xrayDurationMs)}</td><td>{when(vet.lastActivityAt)}</td>
    </tr>)}</tbody>
  </table>;
}

function Inspections({ horse, notes }) {
  if (!horse.inspections.length) return <p className="muted">No inspections recorded yet.</p>;
  return <table className="report-doc-table">
    <caption>Inspections</caption>
    <thead><tr><th scope="col">When</th><th scope="col">Buyer</th><th scope="col">Agent</th>
      <th scope="col">Logged by</th><th scope="col">Seen at parade</th>{notes && <th scope="col">Note</th>}</tr></thead>
    <tbody>{horse.inspections.map((row, index) => <tr key={`${row.inspectedAt}-${index}`}>
      <td>{when(row.inspectedAt)}</td><th scope="row">{row.buyer}</th><td>{row.agent || "None"}</td>
      <td>{row.loggedBy}</td>
      <td>{row.seenAt ? `${when(row.seenAt)}${row.seenBy ? ` by ${row.seenBy}` : ""}` : "Not a parade visit"}</td>
      {notes && <td>{row.note || ""}</td>}
    </tr>)}</tbody>
  </table>;
}

export function ReportDocument({ report }) {
  const { includes, horses } = report;
  const source = report.viewingSource;
  const title = report.owner
    ? `Report for ${report.owner.name}`
    : `Report for ${horses.length === 1 ? "Lot" : "Lots"} ${horses.map(horse => horse.number).join(", ")}`;
  return <article className="report-document">
    <header className="report-doc-head">
      <p className="report-doc-kicker">{report.vendorName} · {report.sale}</p>
      <h2>{title}</h2>
      <p className="muted">Generated {when(report.generatedAt)} (Sydney time)</p>
      {includes.viewing && source.status === "stale" && source.asOf
        && <p>Viewing figures are the latest the Repository has sent, from {when(source.asOf)}.</p>}
    </header>
    <section className="report-doc-summary">
      <h3>Summary</h3>
      {(includes.viewing || includes.inspections) && <SummaryTable report={report}/>}
      {includes.viewing && <p className="report-doc-unavailable">
        Views today and the time of each view are not available yet. {report.perViewDetail.reason}
      </p>}
      {report.note && <blockquote className="report-doc-note">{report.note}</blockquote>}
    </section>
    <section className="report-doc-details">
      <h3>Details</h3>
      {horses.map(horse => <section key={horse.lotId} className="report-doc-horse">
        <h4 className="report-doc-horse-head">{horseName(horse)}</h4>
        {includes.viewing && <Viewing horse={horse}/>}
        {includes.inspections && <Inspections horse={horse} notes={includes.notes}/>}
      </section>)}
    </section>
  </article>;
}

// The report page. Download PDF and Share hand over the server's file
// (vendor.reports.pdf@1), the same one the email attaches; Print stays for a
// paper copy (see @media print in reports.css). The PDF is fetched as the page
// opens rather than on the click, because a phone lets Share run only straight
// from a tap, not after a network wait.
function fileFrom(pdf) {
  const binary = window.atob(pdf.data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new window.File([bytes], pdf.filename, { type: pdf.contentType });
}

export function ReportView({ report, onClose, loadPdf = null }) {
  const panel = React.useRef(null);
  const [file, setFile] = React.useState(null);
  // "loading", "ready", "hidden" (not part of the caller's access) or "failed".
  const [pdfState, setPdfState] = React.useState(loadPdf ? "loading" : "hidden");
  React.useEffect(() => { panel.current?.focus(); }, []);
  React.useEffect(() => {
    if (!loadPdf) return undefined;
    let active = true;
    loadPdf().then(value => {
      if (active) { setFile(fileFrom(value)); setPdfState("ready"); }
    }).catch(failure => {
      if (active) setPdfState(failure?.status === 404 ? "hidden" : "failed");
    });
    return () => { active = false; };
  }, [loadPdf]);
  const canShare = Boolean(file) && typeof window.navigator.canShare === "function"
    && window.navigator.canShare({ files: [file] });

  function download() {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  function share() {
    // A dismissed share sheet rejects; there is nothing to report.
    window.navigator.share({ files: [file], title: file.name }).catch(() => {});
  }

  // Rendered under body, not inside the page: the Reports page's fade-in transform
  // would otherwise be this fixed view's containing block, so it would be the page's
  // height and scroll its top out of sight (seen on dev, 25 Sep 2026).
  return createPortal(<div ref={panel} className="report-view" role="dialog" aria-modal="true" aria-label="Owner report"
    tabIndex={-1} onKeyDown={event => { if (event.key === "Escape") onClose(); }}>
    <div className="report-view-actions">
      {pdfState !== "hidden" && pdfState !== "failed"
        && <button type="button" className="btn primary" data-download-pdf disabled={!file} onClick={download}>
          {file ? "Download PDF" : "Preparing PDF…"}</button>}
      {canShare && <button type="button" className="btn" data-share-pdf onClick={share}>Share</button>}
      <button type="button" className={pdfState === "ready" || pdfState === "loading" ? "btn" : "btn primary"}
        onClick={() => window.print()}>Print</button>
      <button type="button" className="btn" onClick={onClose}>Close report</button>
    </div>
    {pdfState === "failed" && <p role="alert" className="error-text report-view-alert">
      The PDF could not be prepared. Print still works; close the report and open it again to retry.</p>}
    <ReportDocument report={report}/>
  </div>, document.body);
}
